import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import sealionHero from '../images/sealion-7.jpg';
import PageSeo, {
  articleJsonLd,
  breadcrumbJsonLd,
  keywordSets,
  productJsonLd,
} from '../PageSeo';

function Sealion7Detail() {
  const pricePremium = 629000000;
  const pricePerformance = 719000000;
  const formattedStartingPrice = 'Rp 629.000.000';

  const pageTitle = `BYD Sealion 7 Indonesia 2026 | Harga ${formattedStartingPrice}`;
  const pageDescription = `Cek harga BYD Sealion 7 mulai ${formattedStartingPrice} di BYD BSD Serpong. SUV listrik 523 HP dengan varian Premium dan Performance AWD, bebas ganjil genap, dan test drive.`;

  return (
    <Box
      sx={{ padding: { xs: 2, sm: 4 }, maxWidth: '1200px', margin: '0 auto' }}
    >
      <PageSeo
        title={pageTitle}
        description={pageDescription}
        keywords={[
          ...keywordSets.localDealer,
          'BYD Sealion 7 Indonesia',
          'harga BYD Sealion 7',
          'BYD Sealion 7 Performance AWD',
          'SUV listrik BYD 523 HP',
          'Sealion 7 BSD Serpong',
        ]}
        path='/blog/sealion-7-suv-listrik-sporty'
        image={sealionHero}
        type='article'
        jsonLd={[
          productJsonLd({
            name: 'BYD Sealion 7',
            description: pageDescription,
            image: sealionHero,
            path: '/blog/sealion-7-suv-listrik-sporty',
            lowPrice: pricePremium,
            highPrice: pricePerformance,
            offerCount: 2,
          }),
          articleJsonLd({
            title: pageTitle,
            description: pageDescription,
            image: sealionHero,
            path: '/blog/sealion-7-suv-listrik-sporty',
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Blog', path: '/blog' },
            {
              name: 'BYD Sealion 7',
              path: '/blog/sealion-7-suv-listrik-sporty',
            },
          ]),
        ]}
      />

      <Box sx={{ mt: 10 }}>
        {/* H1: Judul Utama SEO */}
        <Typography
          variant='h3'
          component='h1'
          fontWeight='bold'
          gutterBottom
          sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
        >
          BYD Sealion 7: Sport SUV Listrik dengan Performa Buas 523 HP
        </Typography>

        {/* Satu-Satunya Gambar Hero */}
        <Box
          component='img'
          src={sealionHero}
          alt='BYD Sealion 7 Sport SUV Listrik varian Performance AWD tampil sporty di jalanan BSD Serpong'
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: 2,
            mb: 4,
            boxShadow: 3,
          }}
        />

        <Typography
          variant='body1'
          paragraph
          sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
        >
          <strong>BYD Sealion 7</strong> hadir di Indonesia sebagai standar baru
          di kelas Sport SUV listrik. Memadukan kemewahan interior dengan
          performa yang menakjubkan, Sealion 7 ditujukan bagi Anda yang
          menginginkan kecepatan tanpa mengorbankan fungsionalitas keluarga.
          Bagi pemilik di wilayah Jabodetabek, Sealion 7 memberikan privilese{' '}
          <strong>Bebas Ganjil Genap</strong> di jalur protokol Jakarta serta
          keuntungan finansial berupa{' '}
          <strong>Pajak Kendaraan Listrik Murah</strong> yang sangat rendah
          setiap tahunnya.
        </Typography>

        {/* H2: Tabel Perbandingan Varian */}
        <Typography
          variant='h4'
          component='h2'
          fontWeight='bold'
          sx={{ mt: 5, mb: 3 }}
        >
          Harga & Spesifikasi Varian Sealion 7 (Update 2026)
        </Typography>

        <TableContainer component={Paper} elevation={3} sx={{ mb: 4 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#1976d2' }}>
              <TableRow>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Fitur Utama
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Premium (Extended Range)
                </TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                  Performance (AWD)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <strong>Harga OTR</strong>
                </TableCell>
                <TableCell>
                  <strong>Rp 629.000.000</strong>
                </TableCell>
                <TableCell>
                  <strong>Rp 719.000.000</strong>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Daya Maksimum</TableCell>
                <TableCell>230 kW / 267 HP</TableCell>
                <TableCell>390 kW / 523 HP</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Torsi Maksimum</TableCell>
                <TableCell>380 Nm</TableCell>
                <TableCell>690 Nm</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Jarak Tempuh (NEDC)</TableCell>
                <TableCell>567 km</TableCell>
                <TableCell>542 km</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Akselerasi (0-100 km/h)</TableCell>
                <TableCell>6.7 detik</TableCell>
                <TableCell>4.5 detik</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Kapasitas Baterai</TableCell>
                <TableCell colSpan={2} align='center'>
                  82.56 kWh (BYD Blade Battery)
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Kecepatan Maksimum</TableCell>
                <TableCell colSpan={2} align='center'>
                  215 km/h
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Vehicle-to-Load (V2L)</TableCell>
                <TableCell colSpan={2} align='center'>
                  3.3 kW
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Konten SEO Padat Teks */}
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Typography
              variant='h5'
              component='h3'
              fontWeight='bold'
              gutterBottom
            >
              Akselerasi Instan & Teknologi AWD
            </Typography>
            <Typography variant='body1' paragraph>
              Varian <strong>Performance AWD</strong> dilengkapi dengan motor
              ganda yang menghasilkan tenaga fantastis sebesar 523 HP. Dengan
              torsi 690 Nm, Anda bisa merasakan sensasi berkendara mobil sport
              dengan akselerasi 0-100 km/jam hanya dalam 4.5 detik saja. Sistem
              All-Wheel Drive memastikan traksi maksimal di berbagai kondisi
              jalanan Indonesia.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant='h5'
              component='h3'
              fontWeight='bold'
              gutterBottom
            >
              Efisiensi Jarak Tempuh Premium
            </Typography>
            <Typography variant='body1' paragraph>
              Bagi Anda yang mengutamakan jarak tempuh, varian{' '}
              <strong>Premium Extended Range</strong> adalah pilihan ideal.
              Dengan sekali pengisian daya penuh, mobil ini mampu melaju hingga
              567 km (NEDC). Sangat cukup untuk perjalanan luar kota atau
              penggunaan harian selama seminggu penuh tanpa perlu sering mampir
              ke charging station.
            </Typography>
          </Grid>
        </Grid>

        {/* Local SEO & BSD Context */}
        <Typography
          variant='h5'
          component='h3'
          fontWeight='bold'
          sx={{ mt: 5, mb: 2 }}
        >
          Dukungan Ekosistem Charging Station BSD
        </Typography>
        <Typography variant='body1' paragraph>
          Mengendarai BYD Sealion 7 di area Tangerang Selatan semakin nyaman
          berkat masifnya titik <strong>Charging Station BSD</strong>. Mulai
          dari SPKLU di pusat perbelanjaan hingga fasilitas pengisian daya cepat
          di dealer <strong>BYD BSD Serpong</strong>. Teknologi Blade Battery
          yang digunakan juga mendukung pengisian daya cepat, sehingga Anda
          tidak perlu menunggu lama untuk kembali beraktivitas.
        </Typography>

        {/* Internal Linking */}
        <Paper
          variant='outlined'
          sx={{ p: 3, my: 5, backgroundColor: '#f9f9f9' }}
        >
          <Typography variant='h6' fontWeight='bold' gutterBottom>
            Eksplorasi Produk BYD Lainnya:
          </Typography>
          <Typography variant='body2' sx={{ lineHeight: 2 }}>
            • Butuh MPV Keluarga? Cek{' '}
            <Link component={RouterLink} to='/blog/byd-m6-mpv-listrik-keluarga'>
              Harga & Spesifikasi BYD M6
            </Link>
            .
            <br />• Mobil Listrik Paling Value for Money:{' '}
            <Link
              component={RouterLink}
              to='/blog/byd-atto-3-advanced-plus-harga-spesifikasi'
            >
              Update BYD Atto 3 Advanced Plus
            </Link>
            .
            <br />• Lihat daftar harga lengkap di{' '}
            <Link component={RouterLink} to='/pricing'>
              Price List BYD BSD 2026
            </Link>
            .
          </Typography>
        </Paper>

        {/* CTA */}
        <Box
          sx={{
            textAlign: 'center',
            py: 5,
            backgroundColor: '#1a1a1a',
            color: '#fff',
            borderRadius: 2,
          }}
        >
          <Typography
            variant='h4'
            component='h2'
            fontWeight='bold'
            gutterBottom
          >
            Rasakan Torsi 690 Nm Sealion 7!
          </Typography>
          <Typography variant='body1' sx={{ mb: 4, px: 2 }}>
            Booking test drive sekarang di BSD Serpong dan dapatkan hitungan
            cicilan khusus bunga rendah bersama Junius.
          </Typography>
          <Button
            variant='contained'
            size='large'
            href='https://wa.me/6281286864825'
            target='_blank'
            sx={{
              backgroundColor: '#25D366',
              fontWeight: 'bold',
              px: 6,
              py: 1.5,
              borderRadius: '25px',
              fontSize: '1.1rem',
              '&:hover': { backgroundColor: '#128c7e' },
            }}
          >
            Hubungi Junius via WhatsApp
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default Sealion7Detail;
